// Pure geometry for the PCB stator trapezoidal-spiral coil generator.
// No Angular / DI dependencies. All math lives here so it can be unit tested.

import { planCoilPolarities } from './pcb-stator-polarity';
// Stacking-tabs ear geometry. This is a value import used ONLY inside
// `computeBusRouting` at call time (never at module-init), so the geometry ⇄
// stacking-tabs import cycle — stacking-tabs imports `driveAzimuth`/`buildCorners`
// from here — resolves safely (live bindings, evaluated after both modules load).
import { computeStackingEars, assessEarLayout } from './pcb-stator-stacking-tabs';

export interface StatorParams {
  innerRadius: number;
  outerRadius: number;
  numPhases: number;
  numPoles: number;
  numCoils: number;
  traceWidth: number;
  traceClearance: number;
  numLayers: number;
  copperOz: number;
  outerMargin: number;
  innerMargin: number;
}

export interface CoilTurn {
  coilIndex: number;
  arcIndex: number;
  phase: number;
  layer: number;
  currentOut: boolean;
  angle: number;
  outerX: number; outerY: number;   // was ix, iy
  innerX: number; innerY: number;   // was ox, oy
}

export interface CoilArc {
  layer: number;
  phase: number;
  coilIndex: number;
  startX: number; startY: number;
  endX: number;   endY: number;
  radius: number;
  sweepCW: boolean;
}

export interface ViaPoint {
  x: number; y: number;
}

export type TerminalRole = 'drive' | 'internal' | 'return';

export interface TerminalPad {
  x: number; y: number;
  phase: number;
  label: string;
  role: TerminalRole;
  /** True for a stacking-tabs ear joint hole (plated, T3/1.0mm drill, pad on
   *  BOTH faces + both masks) — distinct from an entry-pad terminal (T1). Absent
   *  (undefined) on every non-stacking terminal, so wye/delta/independent-adjacent
   *  emission is byte-identical. */
  ear?: boolean;
}

export interface GerberData {
  turns: CoilTurn[];
  arcs:  CoilArc[];
  terminals: TerminalPad[];
  chainingVias: ChainingVia[];   // Phase 2: 3 per coil (L0-L1, L1-L2, L2-L3)
  exitVias: ExitVia[];           // Phase 2: 1 per coil (L3 → bus tap)
  /** True when the outer margin could not fit every N>=6 hookup boundary —
   *  some layer connections were NOT generated (open circuit), per
   *  `hookupSlotPlan`'s validity (corrective amendment, 2026-07-06). */
  hookupsDropped: boolean;
}

/**
 * SVG large-arc-flag for an origin-centred circular arc (start and end both lie on a
 * circle centred at (0,0)). Returns 1 when the swept angle exceeds 180°, else 0.
 *
 * Every coil/bus arc in this generator is centred on the board origin. An SVG `A`
 * command picks one of two candidate circles via the large-arc/sweep flags; for arcs
 * sweeping more than 180° a hardcoded flag of 0 selects the wrong (off-origin) circle,
 * making the arc bulge across the board. `sweepCW` maps to the SVG sweep-flag.
 */
export function arcLargeArcFlag(
  startX: number, startY: number, endX: number, endY: number, sweepCW: boolean,
): 0 | 1 {
  const a1 = Math.atan2(startY, startX);
  const a2 = Math.atan2(endY, endX);
  let swept = sweepCW ? (a2 - a1) : (a1 - a2);
  swept = ((swept % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return swept > Math.PI ? 1 : 0;
}

/**
 * Sweep direction (CW = true) that traces the MINOR (≤180°) origin-centred arc
 * from (sx,sy) to (ex,ey). At exactly 180° returns false, preserving the existing
 * default so the inherently-180° intra-phase arcs stay byte-identical.
 */
export function shorterSweepCW(sx: number, sy: number, ex: number, ey: number): boolean {
  const a1 = Math.atan2(sy, sx);
  const a2 = Math.atan2(ey, ex);
  // Swept angle if we kept sweepCW = false (matches arcLargeArcFlag's false branch).
  const sweptFalse = (((a1 - a2) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return sweptFalse > Math.PI;  // false-direction arc is the major arc → go the other way
}

/** Circular mean of angles (radians): atan2(mean(sin), mean(cos)). */
export function circularMeanAngle(anglesRad: number[]): number {
  let s = 0, c = 0;
  for (const a of anglesRad) { s += Math.sin(a); c += Math.cos(a); }
  return Math.atan2(s, c);
}

/** Reflect (x,y) about the line through the origin at angle thetaC. */
export function mirrorPoint(x: number, y: number, thetaC: number): { x: number; y: number } {
  const c = Math.cos(2 * thetaC), s = Math.sin(2 * thetaC);
  return { x: c * x + s * y, y: s * x - c * y };
}

/**
 * Reflect a path about the coil centerline thetaC: every endpoint maps
 * θ → 2·thetaC − θ (radius preserved) and every arc's sweep flips.
 * Reflection negates a path's signed moment; combined with traversal from the
 * opposite end it yields an inner→outer spiral CO-ROTATING with the forward path.
 */
export function mirrorPath(
  path: { arcs: CoilArc[]; legs: CoilTurn[] },
  thetaC: number,
): { arcs: CoilArc[]; legs: CoilTurn[] } {
  const arcs = path.arcs.map(a => {
    const s = mirrorPoint(a.startX, a.startY, thetaC);
    const e = mirrorPoint(a.endX, a.endY, thetaC);
    return { ...a, startX: s.x, startY: s.y, endX: e.x, endY: e.y, sweepCW: !a.sweepCW };
  });
  const legs = path.legs.map(l => {
    const o = mirrorPoint(l.outerX, l.outerY, thetaC);
    const i = mirrorPoint(l.innerX, l.innerY, thetaC);
    return { ...l, outerX: o.x, outerY: o.y, innerX: i.x, innerY: i.y, angle: 2 * thetaC - l.angle };
  });
  return { arcs, legs };
}

/**
 * Signed magnetic moment ½∮(x dy − y dx) of a path, in mm² (per unit current).
 * Origin-centred arcs contribute ½r²Δθ (sweepCW=true = CCW in math coords, positive);
 * straight legs contribute ½(x₁y₂ − x₂y₁) — exactly 0 for radial legs, so the
 * outer/inner endpoint-order inconsistency of forward-path step-3 legs is harmless.
 */
export function pathMoment(path: { arcs: CoilArc[]; legs: CoilTurn[] }): number {
  const TWO_PI = 2 * Math.PI;
  const mod = (a: number) => ((a % TWO_PI) + TWO_PI) % TWO_PI;
  let m = 0;
  for (const a of path.arcs) {
    const a1 = Math.atan2(a.startY, a.startX);
    const a2 = Math.atan2(a.endY, a.endX);
    m += 0.5 * a.radius * a.radius * (a.sweepCW ? mod(a2 - a1) : -mod(a1 - a2));
  }
  for (const l of path.legs) {
    m += 0.5 * (l.outerX * l.innerY - l.innerX * l.outerY);
  }
  return m;
}

export interface TurnLimits {
  radial: number;
  angular: number;
  total: number;
}

export interface CoilCorners {
  thetaL: number[];
  thetaR: number[];
  rOuter: number[];
  rInner: number[];
}

export type CoilShape = 'spiral' | 'wedge';

export function computeTurnLimits(params: StatorParams, coilShape: CoilShape = 'spiral'): TurnLimits {
  const { numCoils, traceWidth, traceClearance,
          outerRadius, outerMargin, innerRadius, innerMargin } = params;
  const coilOuter = outerRadius - outerMargin;
  const coilInner = innerRadius + innerMargin;
  const p = traceWidth + traceClearance;
  const delta = traceClearance / coilInner;
  const dTheta = p / coilInner;
  const alpha = Math.PI / numCoils;

  // Option-A spiral: each turn consumes 2p of radial budget (rOuter steps in AND
  // rInner steps out), and turn n−1's middle annulus must still fit the L2↔L3
  // via band: viaLift + pad + clearance + the half-width of the arc above it.
  // (Historical bug: divided by p — fired only at radially-limited configs like
  // 3 coils, inverting the coil middle. Angular binds at all other gated configs.)
  const budget  = coilOuter - coilInner - 2 * traceClearance;
  const viaLift = traceWidth / 2 + traceClearance + T2_PAD_RADIUS;
  const middleMin = viaLift + T2_PAD_RADIUS + traceClearance + traceWidth / 2;
  const radial  = Math.max(0, Math.floor((budget - middleMin) / (2 * p)) + 1);
  const angular = Math.floor((2 * alpha - 2 * delta) / (2 * dTheta));
  const total   = coilShape === 'wedge' ? angular : Math.min(radial, angular);
  return { radial, angular, total };
}

export function buildCoilCorners(
  coilIndex: number,
  params: StatorParams,
  numTurns: number,
): CoilCorners {
  const { numCoils, traceWidth, traceClearance,
          outerRadius, outerMargin, innerRadius, innerMargin } = params;
  const coilOuter = outerRadius - outerMargin;
  const coilInner = innerRadius + innerMargin;
  const p = traceWidth + traceClearance;
  const delta = traceClearance / coilInner;
  const dTheta = p / coilInner;
  const alpha = Math.PI / numCoils;
  const thetaC = (2 * Math.PI * coilIndex) / numCoils - Math.PI / 2;

  const rOuter: number[] = new Array(numTurns);
  const rInner: number[] = new Array(numTurns);
  const thetaL: number[] = new Array(numTurns);
  const thetaR: number[] = new Array(numTurns);

  // Option A spiral (non-crossing): turn 0 is the OUTERMOST, occupying the full
  // band [coilInner+c, coilOuter-c]. Each successive turn shrinks on all four sides
  // (rOuter steps in, rInner steps OUT) so turn t+1 nests strictly inside turn t.
  // The spiral exit at (rInner[n-1], θR[n-1]) sits INSIDE the coil region — exits
  // route through a per-coil through-hole via to F_Cu in the inner-margin bus.
  for (let t = 0; t < numTurns; t++) {
    rOuter[t] = coilOuter - traceClearance - t * p;
    rInner[t] = coilInner + traceClearance + t * p;
    thetaL[t] = thetaC - alpha + delta + t * dTheta;
    thetaR[t] = thetaC + alpha - delta - t * dTheta;
  }

  return { thetaL, thetaR, rOuter, rInner };
}

export function buildWedgeCorners(
  coilIndex: number,
  params: StatorParams,
  numTurns: number,
): CoilCorners {
  const { numCoils, traceWidth, traceClearance,
          outerRadius, outerMargin, innerRadius, innerMargin } = params;
  const coilOuter = outerRadius - outerMargin;
  const coilInner = innerRadius + innerMargin;
  const p = traceWidth + traceClearance;
  const delta = traceClearance / coilInner;
  const dTheta = p / coilInner;
  const alpha = Math.PI / numCoils;
  const thetaC = (2 * Math.PI * coilIndex) / numCoils - Math.PI / 2;

  const rOuter: number[] = new Array(numTurns);
  const rInner: number[] = new Array(numTurns);
  const thetaL: number[] = new Array(numTurns);
  const thetaR: number[] = new Array(numTurns);

  // Wedge: every turn keeps the FULL radial height [coilInner+c, coilOuter-c];
  // turns nest only in θ (a fan), maximising radial active copper.
  for (let t = 0; t < numTurns; t++) {
    rOuter[t] = coilOuter - traceClearance;
    rInner[t] = coilInner + traceClearance;
    thetaL[t] = thetaC - alpha + delta + t * dTheta;
    thetaR[t] = thetaC + alpha - delta - t * dTheta;
  }

  return { thetaL, thetaR, rOuter, rInner };
}

/** Dispatch to the corner generator for the selected coil shape. */
export function buildCorners(
  coilShape: CoilShape,
  coilIndex: number,
  params: StatorParams,
  numTurns: number,
): CoilCorners {
  return coilShape === 'wedge'
    ? buildWedgeCorners(coilIndex, params, numTurns)
    : buildCoilCorners(coilIndex, params, numTurns);
}

export type ChainMargin = 'inner' | 'outer';
export interface ChainTerminal { x: number; y: number; margin: ChainMargin; coil: number; }
export interface ChainJoin { margin: ChainMargin; a: ChainTerminal; b: ChainTerminal; index: number; }
export interface PhaseChain { joins: ChainJoin[]; driveEnd: ChainTerminal; farEnd: ChainTerminal; }

/**
 * Series-chain a phase's coils with same-margin joins, zig-zagging inner/outer.
 * Each coil links its outer entry to its inner exit through its own winding, so
 * consecutive coils are joined on the SAME margin and joins alternate inner/outer.
 * Drive end = phaseCoils[0]'s outer entry. Far end = last coil's free terminal:
 * outer entry when k is even, inner exit when k is odd.
 */
export function planPhaseChain(
  phaseCoils: number[],
  params: StatorParams,
  coilShape: CoilShape,
  n: number,
  flips?: boolean[],
): PhaseChain {
  const entryPadRadiusVal = entryPadRadius(params);
  const exitGeom = exitTerminalGeom(params, coilShape, n);

  // Outer (entry-pad) angle: raw thetaR[0]/thetaL[0] (unchanged).
  const outerAngle = (c: number): number => {
    const cor = buildCorners(coilShape, c, params, n);
    return flips?.[c] ? cor.thetaR[0] : cor.thetaL[0];
  };
  // Inner (exit via) angle: inset inward — mirrored for flips (Task 4b). Must
  // exactly match buildCoilStack's exit placement (shared exitTerminalGeom).
  const innerAngle = (c: number): number => {
    const cor = buildCorners(coilShape, c, params, n);
    return flips?.[c]
      ? cor.thetaR[0] - exitGeom.insetTheta
      : cor.thetaL[0] + exitGeom.insetTheta;
  };
  const innerTerm = (c: number): ChainTerminal => {
    const a = innerAngle(c);
    return { x: exitGeom.radius * Math.cos(a), y: exitGeom.radius * Math.sin(a), margin: 'inner', coil: c };
  };
  const outerTerm = (c: number): ChainTerminal => {
    const a = outerAngle(c);
    return { x: entryPadRadiusVal * Math.cos(a), y: entryPadRadiusVal * Math.sin(a), margin: 'outer', coil: c };
  };

  const k = phaseCoils.length;
  const driveEnd = outerTerm(phaseCoils[0]);
  const joins: ChainJoin[] = [];
  for (let j = 0; j <= k - 2; j++) {
    const margin: ChainMargin = j % 2 === 0 ? 'inner' : 'outer';
    const term = margin === 'inner' ? innerTerm : outerTerm;
    joins.push({ margin, a: term(phaseCoils[j]), b: term(phaseCoils[j + 1]), index: j });
  }
  const last = phaseCoils[k - 1];
  const farEnd = k % 2 === 0 ? outerTerm(last) : innerTerm(last);
  return { joins, driveEnd, farEnd };
}

/** True when a leg has non-negligible length (drops degenerate wedge inter-turn steps). */
function legHasLength(leg: CoilTurn): boolean {
  return Math.hypot(leg.outerX - leg.innerX, leg.outerY - leg.innerY) >= 1e-6;
}

export function buildForwardPath(
  coilIndex: number,
  layerIndex: number,
  phase: number,
  corners: CoilCorners,
): { arcs: CoilArc[]; legs: CoilTurn[] } {
  const arcs: CoilArc[] = [];
  const legs: CoilTurn[] = [];
  const n = corners.rOuter.length;
  if (n === 0) return { arcs, legs };

  for (let t = 0; t < n; t++) {
    const rO = corners.rOuter[t];
    const rI = corners.rInner[t];
    const tL = corners.thetaL[t];
    const tR = corners.thetaR[t];

    // Step 1: OL(t) → IL(t)  (radial leg inward at θ_L)
    legs.push({
      coilIndex, arcIndex: t, phase, layer: layerIndex,
      currentOut: false, angle: tL,
      outerX: rO * Math.cos(tL), outerY: rO * Math.sin(tL),
      innerX: rI * Math.cos(tL), innerY: rI * Math.sin(tL),
    });

    // Step 2: IL(t) → IR(t)  (inner arc CCW in math → sweepCW true after y-flip)
    arcs.push({
      layer: layerIndex, phase, coilIndex,
      startX: rI * Math.cos(tL), startY: rI * Math.sin(tL),
      endX:   rI * Math.cos(tR), endY:   rI * Math.sin(tR),
      radius: rI,
      sweepCW: true,
    });

    if (t === n - 1) break;  // final turn terminator

    // Step 3: IR(t) → OR(t)  (radial leg outward at θ_R)
    legs.push({
      coilIndex, arcIndex: t, phase, layer: layerIndex,
      currentOut: false, angle: tR,
      outerX: rO * Math.cos(tR), outerY: rO * Math.sin(tR),
      innerX: rI * Math.cos(tR), innerY: rI * Math.sin(tR),
    });

    const tLNext = corners.thetaL[t + 1];
    const rONext = corners.rOuter[t + 1];

    // Step 4: OR(t) → (r_o(t), θ_L(t+1))  (outer arc CW in math → sweepCW false)
    arcs.push({
      layer: layerIndex, phase, coilIndex,
      startX: rO * Math.cos(tR),     startY: rO * Math.sin(tR),
      endX:   rO * Math.cos(tLNext), endY:   rO * Math.sin(tLNext),
      radius: rO,
      sweepCW: false,
    });

    // Step 5: (r_o(t), θ_L(t+1)) → OL(t+1)  (short radial inward at θ_L(t+1))
    legs.push({
      coilIndex, arcIndex: t, phase, layer: layerIndex,
      currentOut: false, angle: tLNext,
      outerX: rO     * Math.cos(tLNext), outerY: rO     * Math.sin(tLNext),
      innerX: rONext * Math.cos(tLNext), innerY: rONext * Math.sin(tLNext),
    });
  }

  return { arcs, legs: legs.filter(legHasLength) };
}

/**
 * Time-reversed forward path: walks from inner-right of the innermost turn back
 * to outer-left of the outermost turn. Used for L1, L3 (alternating series chain).
 *
 * Emission order, per spec section "Series-chained coil stack":
 *   Innermost turn (t = n-1):
 *     [arc] inner-right[t] → inner-left[t]   (radius rI[t], sweepCW=false)
 *     [leg] inner-left[t]  → outer-left[t]   (radial outward at θL[t])
 *   Turns t = n-2 .. 0 (each adds 5 segments, ending at outer-left[t]):
 *     [leg] outer-left[t+1]      → (rO[t], θL[t+1])    (short leg outward)
 *     [arc] (rO[t], θL[t+1])     → outer-right[t]      (radius rO[t], sweepCW=true)
 *     [leg] outer-right[t]       → inner-right[t]      (radial inward at θR[t])
 *     [arc] inner-right[t]       → inner-left[t]       (radius rI[t], sweepCW=false)
 *     [leg] inner-left[t]        → outer-left[t]       (radial outward at θL[t])
 */
export function buildReversePath(
  coilIndex: number,
  layerIndex: number,
  phase: number,
  corners: CoilCorners,
): { arcs: CoilArc[]; legs: CoilTurn[] } {
  const arcs: CoilArc[] = [];
  const legs: CoilTurn[] = [];
  const n = corners.rOuter.length;
  if (n === 0) return { arcs, legs };

  for (let t = n - 1; t >= 0; t--) {
    const rO = corners.rOuter[t];
    const rI = corners.rInner[t];
    const tL = corners.thetaL[t];
    const tR = corners.thetaR[t];

    if (t < n - 1) {
      const tLNext = corners.thetaL[t + 1];

      // short leg outward: outer-left[t+1] → (rO[t], θL[t+1])
      legs.push({
        coilIndex, arcIndex: t, phase, layer: layerIndex,
        currentOut: false, angle: tLNext,
        outerX: corners.rOuter[t + 1] * Math.cos(tLNext),
        outerY: corners.rOuter[t + 1] * Math.sin(tLNext),
        innerX: rO * Math.cos(tLNext),
        innerY: rO * Math.sin(tLNext),
      });

      // outer arc: (rO[t], θL[t+1]) → outer-right[t]
      arcs.push({
        layer: layerIndex, phase, coilIndex,
        startX: rO * Math.cos(tLNext), startY: rO * Math.sin(tLNext),
        endX:   rO * Math.cos(tR),     endY:   rO * Math.sin(tR),
        radius: rO,
        sweepCW: true,
      });

      // radial leg inward: outer-right[t] → inner-right[t]
      legs.push({
        coilIndex, arcIndex: t, phase, layer: layerIndex,
        currentOut: false, angle: tR,
        outerX: rO * Math.cos(tR), outerY: rO * Math.sin(tR),
        innerX: rI * Math.cos(tR), innerY: rI * Math.sin(tR),
      });
    }

    // inner arc: inner-right[t] → inner-left[t]
    arcs.push({
      layer: layerIndex, phase, coilIndex,
      startX: rI * Math.cos(tR), startY: rI * Math.sin(tR),
      endX:   rI * Math.cos(tL), endY:   rI * Math.sin(tL),
      radius: rI,
      sweepCW: false,
    });

    // outward leg: outerX/Y = from, innerX/Y = to (the convention is directional, not radial)
    // radial leg outward: inner-left[t] → outer-left[t]
    legs.push({
      coilIndex, arcIndex: t, phase, layer: layerIndex,
      currentOut: false, angle: tL,
      outerX: rI * Math.cos(tL), outerY: rI * Math.sin(tL),
      innerX: rO * Math.cos(tL), innerY: rO * Math.sin(tL),
    });
  }

  return { arcs, legs: legs.filter(legHasLength) };
}

/**
 * Reverse path that truncates mid-turn-0: stops at inner-right[0] (= rInner[0], θR[0]).
 * Drops the final inner arc (step 2 reversed) and final left leg (step 1 reversed) of turn 0.
 * Used for L3 (B_Cu) so the chain endpoint lands at the inner-margin boundary, where
 * the L3 exit via and B_Cu tail trace pick it up for the bus tap.
 */
export function buildTruncatedReversePath(
  coilIndex: number,
  layerIndex: number,
  phase: number,
  corners: CoilCorners,
): { arcs: CoilArc[]; legs: CoilTurn[] } {
  const full = buildReversePath(coilIndex, layerIndex, phase, corners);
  if (full.arcs.length === 0) return full;
  return {
    arcs: full.arcs.slice(0, -1),  // drop inner arc of turn 0
    legs: full.legs.slice(0, -1),  // drop left leg of turn 0
  };
}

export const T2_DRILL_DIAMETER = 0.4;
/** Via annular pad diameter (mm); matches the component's VIA_PAD_MM. */
export const VIA_PAD_MM = 0.6;
export const T2_PAD_RADIUS = VIA_PAD_MM / 2;   // 0.3
/** Fab hole-to-copper minimum: plated-hole EDGE to copper EDGE on layers the
 *  via has no pad on. Distinct from traceClearance (same-layer copper-copper). */
export const HOLE_TO_COPPER_MM = 0.3;

/**
 * Inner-margin bus-arc slot radius (Task 4b). Fixed-pitch ladder from the inner
 * safe-zone floor: slot 0 is one trace half-width above innerSafeMin. The pitch
 * between slots must clear not just a neighbouring trace but a neighbouring
 * slot's F.Cu bridge-via PAD (each non-F.Cu inner arc carries F.Cu bridge pads at
 * its endpoints). A via pad (radius VIA_PAD_MM/2) needs padR + traceClearance +
 * traceWidth/2 between its center and the slot below's arc edge — wider than the
 * bare trace pitch — so the pitch is the max of the two (0.031 mm DRC graze fix,
 * spec-amendment "stagger slots" band). At defaults this is 1.05 mm vs 1.0 mm.
 */
export function innerSlotPitch(params: StatorParams): number {
  const padR = VIA_PAD_MM / 2;
  return Math.max(
    params.traceWidth + params.traceClearance,
    padR + params.traceClearance + params.traceWidth / 2,
  );
}

export function innerSlotRadius(params: StatorParams, slot: number): number {
  const innerSafeMin = params.innerRadius + params.traceClearance;
  return innerSafeMin + params.traceWidth / 2 + slot * innerSlotPitch(params);
}

export interface ExitTerminalGeom {
  /** Radius of the via-class exit junction (between top bus slot and coil arc). */
  radius: number;
  /** Inward angular inset from thetaL[0] (normal) — mirrored to −inset for flips. */
  insetTheta: number;
}

/**
 * Exit-terminal position (Task 4b). The exit is a via-class internal junction
 * placed in the empty annulus between the top inner-bus slot and the innermost
 * coil arc, and inset angularly inward so its pad clears both the bus slot and
 * the neighbouring coil's mirrored exit across the coil boundary. Everything is
 * derived from params — no magic numbers.
 *
 * `_coilShape`/`_n` are reserved: the inner-margin clearance window is shape- and
 * turn-count-independent today (wedge and spiral share the same inner bus ladder and
 * innermost-arc radius), so both are unused — kept in the signature so a future
 * shape-specific exit placement needs no call-site churn.
 */
export function exitTerminalGeom(
  params: StatorParams,
  _coilShape: CoilShape,
  _n: number,
): ExitTerminalGeom {
  const padR = VIA_PAD_MM / 2;
  const topArc = innerSlotRadius(params, params.numPhases - 1);
  const rInner0 = (params.innerRadius + params.innerMargin) + params.traceClearance;
  const clearAbove = params.traceWidth / 2 + params.traceClearance + padR;
  const radius = ((topArc + clearAbove) + (rInner0 - clearAbove)) / 2;
  const insetTheta = (padR + params.traceWidth / 2 + params.traceClearance) / radius;
  return { radius, insetTheta };
}

/** Ladder radii that fit below the exit-via ring: largest count whose top
 *  slot's arc copper edge PLUS one clearance (r + traceWidth/2 + traceClearance)
 *  stays below the exit-via PAD's inner edge (exit.radius − padR). A slot arc
 *  carries no pad of its own — the join pads ARE the exit vias at the exit
 *  radius — so the pad term appears once (on the exit side) and the clearance
 *  once (copper-to-copper), never doubled. The exit ring itself is FIXED by the
 *  first-join band (exitTerminalGeom reads slot(numPhases−1)) — not circular
 *  with this count. At defaults yields 3 (slots 0,1,2); innerMargin 7 mm yields
 *  4 (spec fit table for k=8); innerMargin 6 mm stays 3 (slot 3's edge would
 *  come within 0.225 mm of the exit pad — a sub-clearance graze). */
export function maxInnerSlots(params: StatorParams, coilShape: CoilShape, n: number): number {
  const padR = VIA_PAD_MM / 2;
  const ceiling = exitTerminalGeom(params, coilShape, n).radius - padR;
  let count = 0;
  while (innerSlotRadius(params, count) + params.traceWidth / 2 + params.traceClearance
         <= ceiling + 1e-9) count++;
  return count;
}

/** Inner join arcs the zig-zag chain needs: numPhases × ceil((k−1)/2). */
export function innerJoinDemand(numCoils: number, numPhases: number): number {
  const k = Math.floor(numCoils / numPhases);
  return numPhases * Math.ceil((k - 1) / 2);
}

/** Terminal pad/hole sizes (mm) — the outer bus keep-away derivation reads these
 *  the same way the delta window did. Defaults to the 'solder' style. */
export interface TermDims { padMm: number; holeMm: number; }

/** Fixed max clearance of the entry-pad ring above the coil's outer edge (mm).
 *  Caps the coil→pad gap so it stops growing with the outer margin. ≥ the L1↔L2
 *  via band + entry-pad half-width + clearance (~2.35mm min) AND = the default
 *  5mm board's half-margin, so the default stays byte-stable. Spec 2026-07-05. */
export const OUTER_PAD_GAP = 2.5;

/** Radius of the entry-pad ring: coil outer edge + min(half-margin, OUTER_PAD_GAP).
 *  At the 5mm default this is the old `outerRadius − margin/2` (57.5); for larger
 *  margins it pins to a fixed clearance instead of receding with the margin. */
export function entryPadRadius(params: StatorParams): number {
  const coilOuter = params.outerRadius - params.outerMargin;
  return coilOuter + Math.min(params.outerMargin / 2, OUTER_PAD_GAP);
}

/** Phase-label letters for terminal naming (U, V, W, …). Shared default so the
 *  independent-mode return pads label the same way the component's entry pads do. */
export const PHASE_LABEL_LETTERS = ['U', 'V', 'W', 'X', 'Y', 'Z'] as const;

/** Angular offset (rad) from a phase's drive (+) pad to its return (−) pad in the
 *  6-lead 'independent' winding. Both pads share `entryPadRadius`; the return pad
 *  sits one pad-plus-two-clearances of arc length away — pad edge to pad edge is a
 *  full copper-copper clearance. Derived, not magic: arc length / radius. */
export function returnPadOffsetRad(params: StatorParams, padMm: number): number {
  return (padMm + 2 * params.traceClearance) / entryPadRadius(params);
}

/**
 * Drive-pad azimuth for phase p — the first-coil entry angle. This is the SINGLE
 * source of truth that `computeReturnPads` (adjacent 6-lead return pads) AND the
 * stacking-tabs ear placement (`computeStackingEars`) both consume, so a return
 * pad and an ear can never derive a different drive azimuth than the drive pad
 * they attach to. `thetaL[0]`/`thetaR[0]` are turn-0 and shape-independent
 * (spiral == wedge), so no `coilShape` argument is needed; coil index p is
 * phase p's first coil. Flips default to `planCoilPolarities(params).flip`.
 */
export function driveAzimuth(params: StatorParams, phase: number, flips?: boolean[]): number {
  const effectiveFlips = flips ?? planCoilPolarities(params).flip;
  const cor = buildCorners('spiral', phase, params, 1);
  return effectiveFlips?.[phase] ? cor.thetaR[0] : cor.thetaL[0];
}

/**
 * Return-pad positions for the 6-lead 'independent' winding — one per phase,
 * adjacent to that phase's drive (+) pad: same radius (`entryPadRadius`), azimuth
 * offset by `returnPadOffsetRad`. Role 'return', label 'U−'|'V−'|'W−'. The drive-pad
 * azimuth is the phase's first-coil entry angle (thetaL[0], or its mirror thetaR[0]
 * for a flipped coil) under the default polarity plan — matching `computeCoilGerberData`'s
 * emitted drive terminals. The plated hole at each position is the F.Cu↔slot-layer
 * transition; the F.Cu pad here joins `GerberData.terminals` while the slot-layer side
 * arrives via `computeBusRouting`'s `extraPadTerminals` at the identical coordinate.
 *
 * `flips` (optional, Task-5 coupling audit): `computeCoilGerberData` and
 * `computeBusRouting` both accept an explicit `flips` override and otherwise
 * default to `planCoilPolarities(params).flip`; this function previously had
 * NO way to receive the same override, so a caller passing non-default flips
 * to the other two (e.g. a future test fixture) would silently get a return
 * pad computed from the DEFAULT flip state instead — an API asymmetry that
 * could desync the emitted return pad from the drive pad it sits beside. Every
 * live call site today (the component, the DRC generator) always passes
 * `undefined` everywhere, so all three functions already agree in practice —
 * this parameter closes the latent gap structurally rather than by
 * coincidence. See `PcbStatorComponent.coilFlips`, which threads ONE shared
 * flips array into `computeCoilGerberData`, `computeBusRouting`, and this
 * function for the real preview/Gerber path.
 */
export function computeReturnPads(
  params: StatorParams,
  termDims: { padMm: number; holeMm: number } = { padMm: 1.5, holeMm: 0.8 },
  flips?: boolean[],
): TerminalPad[] {
  const offset = returnPadOffsetRad(params, termDims.padMm);
  const r = entryPadRadius(params);
  const effectiveFlips = flips ?? planCoilPolarities(params).flip;
  const pads: TerminalPad[] = [];
  for (let p = 0; p < params.numPhases; p++) {
    // Drive azimuth via the shared `driveAzimuth` helper — the exact source the
    // stacking-tabs ears also consume, so a return pad can never diverge from
    // the drive pad it sits beside.
    const driveAngle = driveAzimuth(params, p, effectiveFlips);
    const a = driveAngle + offset;
    const letter = PHASE_LABEL_LETTERS[p] ?? String(p);
    pads.push({ x: r * Math.cos(a), y: r * Math.sin(a), phase: p, label: `${letter}−`, role: 'return' });
  }
  return pads;
}

/**
 * Outer-margin bus keep-away band (corrective 2026-07-04). Hoists the delta fix's
 * window derivation so ALL outer-bus nets (wye, delta cycles, phase-chain joins)
 * share ONE source of truth for where copper may live above the entry-pad band.
 *
 *   hardMin  — lower bound: max of the foreign entry-HOLE keep-away (the plated
 *              barrel bonds every layer, so an In1/In2/B.Cu arc must clear each T1
 *              hole by drill/2 + HOLE_TO_COPPER + trace/2, measured from the entry
 *              pad ring) and the L1↔L2 via band (`outerSafeMin`).
 *   safeMax  — upper bound: board-edge safe max (`outerSafeMax`).
 * Foreign entry PADS sit on F.Cu; outer-bus arcs ride In1/In2/B.Cu only, so the pad
 * edge is a same-layer constraint on each arc's own net (harmless) — the binding
 * lower bound is the hole keep-away, exactly as in the delta block it replaces.
 */
export function outerBusBand(params: StatorParams, termDims: TermDims): { hardMin: number; safeMax: number } {
  const coilOuter = params.outerRadius - params.outerMargin;
  const l12ViaKeepaway = T2_DRILL_DIAMETER / 2 + HOLE_TO_COPPER_MM + params.traceWidth / 2;
  const outerSafeMin = coilOuter + params.traceClearance + T2_PAD_RADIUS + l12ViaKeepaway;
  const outerSafeMax = params.outerRadius - params.traceClearance;
  const entryPadRadiusVal = entryPadRadius(params);
  // A plated T1 barrel is copper on EVERY layer, so a foreign hole binds twice:
  // the fab hole-to-copper minimum (drill edge to copper) AND the same-layer
  // copper-copper clearance to the barrel wall. Take the max — strictly at least
  // as strong as the old delta derivation (which used HOLE_TO_COPPER_MM alone
  // and parked the arc edge boundary-exact on the keep-away radius).
  const holeClear = Math.max(HOLE_TO_COPPER_MM, params.traceClearance);
  const aboveHole = entryPadRadiusVal + termDims.holeMm / 2 + holeClear + params.traceWidth / 2;
  const hardMin = Math.max(aboveHole, outerSafeMin);
  return { hardMin, safeMax: outerSafeMax };
}

/**
 * Outer-margin bus-slot radius (corrective 2026-07-04). Fixed-pitch ladder ABOVE
 * the entry-pad keep-away band, mirroring the inner allocator. Slot 0 is the delta
 * fix's `hardMin` derivation (`outerBusBand`); pitch reuses `innerSlotPitch` (each
 * slot's arc must clear a neighbour's selective via PAD, not just a bare trace).
 */
export function outerSlotRadius(params: StatorParams, termDims: TermDims, slot: number): number {
  return outerBusBand(params, termDims).hardMin + slot * innerSlotPitch(params);
}

/** Outer-margin slot count: largest count whose top slot's arc copper edge PLUS
 *  one clearance (r + traceWidth/2 + traceClearance) stays below `outerSafeMax`.
 *  Same ratified-bound style as `maxInnerSlots`. Defaults: 1 slot (~6.1 mm outer
 *  margin → 2). Each (slot × non-F.Cu layer) is one allocatable pool pair. */
export function maxOuterSlots(params: StatorParams, termDims: TermDims): number {
  const { safeMax } = outerBusBand(params, termDims);
  let count = 0;
  while (outerSlotRadius(params, termDims, count) + params.traceWidth / 2 + params.traceClearance
         <= safeMax + 1e-9) count++;
  return count;
}

/**
 * Inward-fan angle for the boundary-j (j>=1) hookup tail (tail-fan amendment
 * ef41b62, post Task-2 probe re-run). The tail segment A=(rOuter[0], θR0) →
 * B=(slotR, θR0 − fan) must keep point-to-segment distance
 *   d = slotR·sin(fan)·(l12ViaRadius − rOuter[0]) / |B − A|
 * ≥ bound = T2_DRILL_DIAMETER/2 + max(HOLE_TO_COPPER_MM, traceClearance) +
 * traceWidth/2 (0.95mm at defaults — Amendment II addenda ruling 2, the
 * `outerBusBand` hole-keep-away precedent; a boundary-exact solve of the bare
 * fab minimum left one raster graze at margin 5) from the coil's own
 * boundary-0 via hole at (l12ViaRadius, θR0). Solved EXACTLY for
 * the minimum fan: with K = slotR·(l12ViaRadius − rOuter[0])/bound the
 * constraint K·sin(fan) = |B−A| squares to a quadratic in cos(fan); the +
 * root is the small-fan solution. (The amendment sketches a seeded two-pass
 * refinement, but that under-converges — measured d = 0.724 mm < 0.75 mm at
 * margin-7 defaults — so this helper solves the constraint equation exactly,
 * satisfying the amendment's arbiter clause "computed fan must yield
 * measured d ≥ bound".) Per-boundary ladder: fan_j = fanFloor +
 * (j−1)·hookupOffsetθ keeps multiple tails (N=8) laddered. Params-derived —
 * no magic constants. termDims is part of the stable signature (slotR is
 * termDims-derived by the caller via hookupSlotPlan). Feasibility (bound ≤
 * its geometric cap l12ViaRadius − rOuter[0]) is guaranteed by
 * `hookupSlotPlan`'s guard — an infeasible bound never reaches this solve;
 * the clamps below are float-noise safety only.
 */
export function hookupTailFan(
  params: StatorParams,
  _termDims: TermDims,
  slotR: number,
  boundary: number,
): number {
  const coilOuter = params.outerRadius - params.outerMargin;
  const rA = coilOuter - params.traceClearance;                              // rOuter[0], both shapes
  const l12ViaRadius = coilOuter + params.traceClearance + T2_PAD_RADIUS;    // hole-0 radius
  const bound = T2_DRILL_DIAMETER / 2 + Math.max(HOLE_TO_COPPER_MM, params.traceClearance) + params.traceWidth / 2;
  const K = slotR * (l12ViaRadius - rA) / bound;
  const disc = Math.max(0, (rA * slotR) ** 2 - K * K * (rA * rA + slotR * slotR) + K ** 4);
  const cosFan = Math.min(1, (rA * slotR + Math.sqrt(disc)) / (K * K));
  const fanFloor = Math.acos(cosFan);
  const offsetWidth = params.traceWidth + 2 * params.traceClearance + T2_DRILL_DIAMETER;
  const hookupOffsetTheta = offsetWidth / l12ViaRadius;
  return fanFloor + (boundary - 1) * hookupOffsetTheta;
}

/** Pool-slot plan for the N>=6 hookup boundaries (corrective amendment,
 *  2026-07-06). Boundary 0 stays on the legacy l12ViaRadius ring (N=4
 *  byte-stability); boundaries j>=1 take the LOWEST outer-band slots, one
 *  each, admission ahead of wye/delta/joins. arcLayer is fixed (2j+2) — only
 *  the slot is allocated. */
export function hookupSlotPlan(
  params: StatorParams,
  termDims: TermDims,
): { valid: boolean; slots: Array<{ boundary: number; slot: number; radius: number; arcLayer: number }> } {
  const boundaries = Math.max(0, params.numLayers / 2 - 2);
  const slots: Array<{ boundary: number; slot: number; radius: number; arcLayer: number }> = [];
  if (boundaries > maxOuterSlots(params, termDims)) return { valid: false, slots: [] };
  if (boundaries > 0) {
    // Tail-bound feasibility (spec 07af7a7): hookupTailFan's clearance bound
    // is geometrically capped by l12ViaRadius − rOuter[0] (= 2·traceClearance
    // + T2_PAD_RADIUS) — the hole-0→tail-start distance the tail segment can
    // never exceed. Same strictness as the exact solve (its discriminant is
    // ≥ 0 ⇔ bound ≤ cap): an infeasible bound is an honest hookupsDropped
    // drop, NEVER a clamped fan of 0 (which would run the tail radially
    // through hole 0 — a silent intra-coil short).
    const bound = T2_DRILL_DIAMETER / 2 + Math.max(HOLE_TO_COPPER_MM, params.traceClearance) + params.traceWidth / 2;
    const cap = 2 * params.traceClearance + T2_PAD_RADIUS;
    if (bound > cap) return { valid: false, slots: [] };
  }
  for (let b = 0; b < boundaries; b++) {
    slots.push({
      boundary: b + 1, slot: b,
      radius: outerSlotRadius(params, termDims, b),
      arcLayer: 2 * (b + 1) + 2,
    });
  }
  return { valid: true, slots };
}

export interface CoilStack {
  arcs: CoilArc[];
  legs: CoilTurn[];
  chainingVias: ChainingVia[];
  exitVia: ExitVia;
  terminal: TerminalPad;
}

/**
 * Compose one coil's complete even-N-layer series chain (specs 2026-07-02 §2
 * and 2026-07-06 §2): N/2 pairs of forward + mirrored-reverse spirals, inner
 * rung-via ladder, fanned outer hookups. At numLayers=4 this reproduces the
 * 2026-07-02 chain byte-for-byte.
 * All layers CO-ROTATE: even layers are forward spirals; odd layers are
 * MIRRORED reverse spirals (reflection about thetaC × traversal from the
 * inner end preserves circulation). Chain surgery (dropped arcs, via
 * keep-aways) happens here — the path builders themselves are frozen.
 *
 * Drill keep-away invariant: no copper on a layer outside a via's pad set
 * comes within drillRadius + HOLE_TO_COPPER_MM (0.3, the fab hole-to-copper
 * minimum — distinct from same-layer copper-copper traceClearance) of that
 * via's hole.
 */
export function buildCoilStack(
  c: number,
  params: StatorParams,
  coilShape: CoilShape,
  n: number,
  flip: boolean,
  phaseLabels: readonly string[],
  termDims: TermDims = { padMm: 1.5, holeMm: 0.8 },
): CoilStack {
  const phase = c % params.numPhases;
  const corners = buildCorners(coilShape, c, params, n);
  const thetaC = (2 * Math.PI * c) / params.numCoils - Math.PI / 2;

  const coilOuter = params.outerRadius - params.outerMargin;
  const coilInner = params.innerRadius + params.innerMargin;
  const entryPadRadiusVal = entryPadRadius(params);
  const exitGeom = exitTerminalGeom(params, coilShape, n);
  const l12ViaRadius = coilOuter + params.traceClearance + T2_PAD_RADIUS;
  const viaLift = params.traceWidth / 2 + params.traceClearance + T2_PAD_RADIUS;
  const hookupSlots = hookupSlotPlan(params, termDims);

  const rIN = corners.rInner[n - 1];
  const rON = corners.rOuter[n - 1];
  const tL0 = corners.thetaL[0], tR0 = corners.thetaR[0];
  const tLN = corners.thetaL[n - 1], tRN = corners.thetaR[n - 1];

  if (params.numLayers < 4 || params.numLayers > 8 || params.numLayers % 2 !== 0) {
    throw new Error(`numLayers must be an even count in 4..8 (got ${params.numLayers})`);
  }
  const pairCount = params.numLayers / 2;

  const offsetWidth = params.traceWidth + 2 * params.traceClearance + T2_DRILL_DIAMETER;
  // Generalized rung clamp: all pairCount−1 offset rungs must fit inside the
  // innermost arc's half-span (at N=4 this is exactly the old (tRN−tLN)/2).
  // Degenerate-span configs park the rungs mid-arc and rely on the DRC to
  // flag the (unbuildable) board loudly.
  const offsetTheta = Math.min(offsetWidth / rIN, (tRN - tLN) / (2 * Math.max(1, pairCount - 1)));
  // (The old per-boundary hookupOffsetTheta fan now lives in hookupTailFan —
  // derived from the hole-0 clearance bound instead of assumed.)

  // Wedge via placement (spec 2026-07-03): wedge turns keep FULL radial height,
  // so the in-band inner-via ring (rIN) sits ON In2/B.Cu copper of every turn —
  // the plated barrels would bond non-pad layers. For wedge, both inner chaining
  // vias relocate radially to the same formula-derived inner-margin ring the exit
  // vias use (exitTerminalGeom), keeping their angles; straight radial tails
  // bridge the spiral ends to the vias. Spiral keeps the in-band placement (its
  // tapering turns leave the middle annulus empty and legal).
  const isWedge = coilShape === 'wedge';
  const via01Radius = isWedge ? exitGeom.radius : rIN;
  const via23Radius = isWedge ? exitGeom.radius : rIN + viaLift;

  const arcs: CoilArc[] = [];
  const legs: CoilTurn[] = [];
  const pushLeg = (layer: number, arcIndex: number, from: { x: number; y: number }, to: { x: number; y: number }, angle: number) => {
    legs.push({
      coilIndex: c, arcIndex, phase, layer, currentOut: false, angle,
      outerX: from.x, outerY: from.y, innerX: to.x, innerY: to.y,
    });
  };
  const polar = (r: number, a: number) => ({ x: r * Math.cos(a), y: r * Math.sin(a) });

  const chainingVias: ChainingVia[] = [];
  const layerName = (i: number) => layerNameByIndex(i, params.numLayers);
  let exitPos!: { x: number; y: number };

  for (let j = 0; j < pairCount; j++) {
    const fwd = 2 * j, ret = 2 * j + 1;
    const isLast = j === pairCount - 1;
    const tViaJ = tRN - j * offsetTheta;              // rung angle (j=0 → tRN)
    const rViaJ = j === 0 ? via01Radius : via23Radius; // rungs ≥1 share the lifted radius

    // ── Forward layer 2j ─────────────────────────────────────────────────────
    if (j === 0) {
      // L0 pattern: full forward + entry connector (+ wedge tail). Unchanged.
      const lf = buildForwardPath(c, 0, phase, corners);
      arcs.push(...lf.arcs); legs.push(...lf.legs);
      pushLeg(0, -1, polar(entryPadRadiusVal, tL0), polar(corners.rOuter[0], tL0), tL0);
      if (isWedge) pushLeg(0, -2, polar(rIN, tRN), polar(rViaJ, tRN), tRN);
    } else {
      // L2 pattern generalized: forward whose final inner arc stops early at
      // this pair's rung angle, plus the radial jog to the rung via.
      const lf = buildForwardPath(c, fwd, phase, corners);
      const lfLast = lf.arcs[lf.arcs.length - 1];      // final inner arc at rIN
      lfLast.endX = rIN * Math.cos(tViaJ);
      lfLast.endY = rIN * Math.sin(tViaJ);
      arcs.push(...lf.arcs); legs.push(...lf.legs);
      pushLeg(fwd, -2, polar(rIN, tViaJ), polar(rViaJ, tViaJ), tViaJ);
    }

    // ── Rung via j (layers 2j ↔ 2j+1) ────────────────────────────────────────
    chainingVias.push({
      coilIndex: c, position: polar(rViaJ, tViaJ),
      layers: [layerName(fwd), layerName(ret)], tool: 0.4,
      bridge: `L${fwd}-L${ret}`,
    });

    // ── Return layer 2j+1 ────────────────────────────────────────────────────
    if (!isLast) {
      if (j === 0) {
        // L1 pattern verbatim: mirrored reverse minus its first inner arc.
        const lr = mirrorPath(buildReversePath(c, ret, phase, corners), thetaC);
        arcs.push(...lr.arcs.slice(1)); legs.push(...lr.legs);
        if (isWedge) pushLeg(ret, -2, polar(rViaJ, tRN), polar(rIN, tRN), tRN);
      } else {
        // Intermediate return at an offset rung (N≥6 only): full mirrored
        // reverse re-anchored to rung j — the L3 surgery minus truncation.
        // Pre-mirror image of tViaJ is 2·thetaC − tViaJ = tLN + j·offsetTheta.
        const base = buildReversePath(c, ret, phase, corners);
        const preViaJ = 2 * thetaC - tViaJ;
        base.arcs.shift();                              // innermost inner arc lives on the fwd side
        const lg0 = base.legs[0];                       // innermost leg-out at tLN
        lg0.angle = preViaJ;
        lg0.outerX = rViaJ * Math.cos(preViaJ); lg0.outerY = rViaJ * Math.sin(preViaJ);
        lg0.innerX = rON * Math.cos(preViaJ);   lg0.innerY = rON * Math.sin(preViaJ);
        const lg1 = base.legs[1];                       // spiral shape only: short inter-turn leg at tLN
        if (lg1 && Math.abs(lg1.angle - tLN) < 1e-9) {
          lg1.angle = preViaJ;
          lg1.outerX = rON * Math.cos(preViaJ);                   lg1.outerY = rON * Math.sin(preViaJ);
          lg1.innerX = corners.rOuter[n - 2] * Math.cos(preViaJ); lg1.innerY = corners.rOuter[n - 2] * Math.sin(preViaJ);
        }
        const a0 = base.arcs[0];                        // first remaining arc started at tLN
        a0.startX = a0.radius * Math.cos(preViaJ);
        a0.startY = a0.radius * Math.sin(preViaJ);
        const lr = mirrorPath(base, thetaC);
        arcs.push(...lr.arcs); legs.push(...lr.legs);
      }

      // ── Hookup boundary j → j+1: fanned via + arc on layer 2j+2 ────────────
      if (j === 0) {
        // Boundary 0: N=4 byte-stability — unchanged verbatim, always at l12ViaRadius.
        const tHookJ = tR0;                               // j=0 → tR0 (today's tail)
        pushLeg(ret, -2, polar(corners.rOuter[0], tR0), polar(l12ViaRadius, tHookJ), tHookJ);
        chainingVias.push({
          coilIndex: c, position: polar(l12ViaRadius, tHookJ),
          layers: [layerName(ret), layerName(fwd + 2)], tool: 0.4,
          bridge: `L${ret}-L${ret + 1}`,
        });
        arcs.push({
          layer: fwd + 2, phase, coilIndex: c,
          startX: l12ViaRadius * Math.cos(tHookJ), startY: l12ViaRadius * Math.sin(tHookJ),
          endX: l12ViaRadius * Math.cos(tL0),      endY: l12ViaRadius * Math.sin(tL0),
          radius: l12ViaRadius,
          sweepCW: false,                                 // toward θL[0] is −θ in math coords
        });
        pushLeg(fwd + 2, -2, polar(l12ViaRadius, tL0), polar(corners.rOuter[0], tL0), tL0);
      } else {
        const hs = hookupSlots.slots.find(h => h.boundary === j);
        if (!hs) {
          // Honest drop: no slot fits — emit no hookup (open circuit is honest;
          // a fused short is not). computeCoilGerberData flags hookupsDropped.
        } else {
          const slotR = hs.radius;
          // Inward fan DERIVED from the hole-0 clearance bound (tail-fan
          // amendment ef41b62) — the fixed per-boundary hookupOffsetTheta fan
          // measured 0.568mm < 0.75mm at the l12 crossing (Task-2 probe re-run).
          const tVia = tR0 - hookupTailFan(params, termDims, slotR, j);
          // Slanted tail (ret layer): spiral end -> pool via.
          pushLeg(ret, -2, polar(corners.rOuter[0], tR0), polar(slotR, tVia), tVia);
          chainingVias.push({
            coilIndex: c, position: polar(slotR, tVia),
            layers: [layerName(ret), layerName(fwd + 2)], tool: 0.4,
            bridge: `L${ret}-L${ret + 1}`,
          });
          // Pad-clearing drop offset (amendment formula; conservative by design).
          const padClear = termDims.padMm / 2 + params.traceClearance + params.traceWidth / 2;
          const epsDrop = (padClear / entryPadRadiusVal) *
            ((slotR - corners.rOuter[0]) / (slotR - entryPadRadiusVal));
          arcs.push({
            layer: fwd + 2, phase, coilIndex: c,
            startX: slotR * Math.cos(tVia),              startY: slotR * Math.sin(tVia),
            endX: slotR * Math.cos(tL0 + epsDrop),       endY: slotR * Math.sin(tL0 + epsDrop),
            radius: slotR,
            sweepCW: false,
          });
          pushLeg(fwd + 2, -2, polar(slotR, tL0 + epsDrop), polar(corners.rOuter[0], tL0), tL0);
        }
      }
    } else {
      // Last pair: L3 pattern verbatim — mirrored truncated reverse with the
      // pre-mirror surgery, re-anchored to this pair's rung, then the exit tail.
      const l3base = buildTruncatedReversePath(c, ret, phase, corners);
      const preVia = 2 * thetaC - tViaJ;
      l3base.arcs.shift();
      const lg0 = l3base.legs[0];
      lg0.angle = preVia;
      lg0.outerX = rViaJ * Math.cos(preVia); lg0.outerY = rViaJ * Math.sin(preVia);
      lg0.innerX = rON * Math.cos(preVia);   lg0.innerY = rON * Math.sin(preVia);
      const lg1 = l3base.legs[1];
      if (lg1 && Math.abs(lg1.angle - tLN) < 1e-9) {
        lg1.angle = preVia;
        lg1.outerX = rON * Math.cos(preVia);                   lg1.outerY = rON * Math.sin(preVia);
        lg1.innerX = corners.rOuter[n - 2] * Math.cos(preVia); lg1.innerY = corners.rOuter[n - 2] * Math.sin(preVia);
      }
      const a0 = l3base.arcs[0];
      a0.startX = a0.radius * Math.cos(preVia);
      a0.startY = a0.radius * Math.sin(preVia);
      const lr = mirrorPath(l3base, thetaC);
      arcs.push(...lr.arcs); legs.push(...lr.legs);
      const exitAngle = tL0 + exitGeom.insetTheta;
      const exitPos_ = polar(exitGeom.radius, exitAngle);
      pushLeg(ret, -2, polar(corners.rInner[0], tL0), exitPos_, exitAngle);
      exitPos = exitPos_;
    }
  }
  const coilNum = Math.floor(c / params.numPhases) + 1;
  const label = phaseLabels[phase] ?? String(phase);
  const exitVia: ExitVia = {
    coilIndex: c, position: exitPos,
    layers: ['B.Cu', 'F.Cu'], tool: 0.4, label: `${label}${coilNum}-`,
  };
  const entry = polar(entryPadRadiusVal, tL0);
  const terminal: TerminalPad = {
    x: entry.x, y: entry.y, phase,
    label: `${label}${coilNum}+`,
    role: c < params.numPhases ? 'drive' : 'internal',
  };

  if (flip) {
    const m = mirrorPath({ arcs, legs }, thetaC);
    const mv = (p: { x: number; y: number }) => mirrorPoint(p.x, p.y, thetaC);
    return {
      arcs: m.arcs,
      legs: m.legs,
      chainingVias: chainingVias.map(v => ({ ...v, position: mv(v.position) })),
      exitVia: { ...exitVia, position: mv(exitVia.position) },
      terminal: { ...terminal, ...mv(terminal) },
    };
  }
  return { arcs, legs, chainingVias, exitVia, terminal };
}

export function computeCoilGerberData(
  params: StatorParams,
  phaseLabels: readonly string[],
  coilShape: CoilShape = 'spiral',
  flips?: boolean[],
  termDims: TermDims = { padMm: 1.5, holeMm: 0.8 },
): GerberData {
  const turns: CoilTurn[] = [];
  const arcs:  CoilArc[]  = [];
  const terminals: TerminalPad[] = [];
  const chainingVias: ChainingVia[] = [];
  const exitVias: ExitVia[] = [];
  const hookupsDropped = !hookupSlotPlan(params, termDims).valid;

  const limits = computeTurnLimits(params, coilShape);
  const n = limits.total;
  if (n < 2) return { turns, arcs, terminals, chainingVias, exitVias, hookupsDropped };

  const effectiveFlips = flips ?? planCoilPolarities(params).flip;

  for (let c = 0; c < params.numCoils; c++) {
    const stack = buildCoilStack(c, params, coilShape, n, effectiveFlips?.[c] ?? false, phaseLabels, termDims);
    arcs.push(...stack.arcs);
    turns.push(...stack.legs);
    chainingVias.push(...stack.chainingVias);
    exitVias.push(stack.exitVia);
    terminals.push(stack.terminal);
  }

  return { turns, arcs, terminals, chainingVias, exitVias, hookupsDropped };
}

export type WindingType = 'wye' | 'delta' | 'independent';

// ── Phase 2: layer + drill + via types ──────────────────────────────────────

export type CopperLayer =
  | 'F.Cu' | 'In1.Cu' | 'In2.Cu' | 'In3.Cu' | 'In4.Cu' | 'In5.Cu' | 'In6.Cu' | 'B.Cu';

export type DrillTool = 0.8 | 0.4;

export interface ChainingVia {
  coilIndex: number;
  position: { x: number; y: number };
  layers: [CopperLayer, CopperLayer];   // exactly two layers carry pads
  tool: DrillTool;
  /** Which inter-layer transition this realizes, e.g. 'L0-L1', 'L1-L2', 'L2-L3'. */
  bridge: `L${number}-L${number}`;
}

export interface ExitVia {
  coilIndex: number;
  position: { x: number; y: number };
  layers: [CopperLayer, CopperLayer];   // [B.Cu, F.Cu]
  tool: DrillTool;                       // 0.4 (via-class — internal junction, Task 4b)
  label: string;                         // e.g. 'U1-' (matches entry pad sibling)
}

export interface BridgeVia {
  position: { x: number; y: number };
  layers: [CopperLayer, CopperLayer];   // [F.Cu, <home layer>]
  tool: DrillTool;                       // 0.4
  arcId: string;                         // e.g. 'intra-phase-1-start', 'delta-cycle-2-end'
}

/** Layer name for a given 0-based copper-stack index, sized by total layers. */
export function layerNameByIndex(i: number, numLayers: number): CopperLayer {
  if (i <= 0) return 'F.Cu';
  if (i >= numLayers - 1) return 'B.Cu';
  return `In${i}.Cu` as CopperLayer;
}

/** All non-F.Cu copper layers, B.Cu last — the bus pools' admission order. */
export function nonFPoolLayers(numLayers: number): CopperLayer[] {
  const out: CopperLayer[] = [];
  for (let i = 1; i < numLayers - 1; i++) out.push(layerNameByIndex(i, numLayers));
  out.push('B.Cu');
  return out;
}

/**
 * Static home-layer assignment from spec section "F_Cu bridges for the bus".
 * Phase 0 stays on F.Cu (reference). Other phases hop to layers whose annulus
 * is empty for that side.
 */
export function busArcHomeLayer(
  kind: 'intra-phase' | 'wye-neutral' | 'delta-cycle',
  phase: number,
  numLayers = 4,
): CopperLayer {
  if (kind === 'intra-phase') {
    return (['F.Cu', 'In1.Cu', 'In2.Cu'] as const)[phase] ?? 'F.Cu';
  }
  if (kind === 'delta-cycle') {
    // Delta bus (final-review fix 2026-07-03): the three cycle arcs mutually overlap
    // in angle, so a single-layer (B.Cu) routing fuses foreign nets (a fab short the
    // seedless DRC missed). Route ONE arc PER LAYER on In1/In2/B.Cu — one delta net
    // per layer means foreign same-layer pads cannot exist. F.Cu is skipped (it holds
    // the entry connectors/pads), so phase p rides layer index (p mod (numLayers−1))+1.
    return layerNameByIndex(1 + (phase % Math.max(1, numLayers - 1)), numLayers);
  }
  // Wye neutral: single net — its self-overlaps are harmless, so it stays entirely on
  // B.Cu (the outer margin is empty in the new mirrored chain, and the plated entry
  // hole is the F.Cu↔B.Cu transition — no bridge vias needed).
  return 'B.Cu';
}

export interface BusArc {
  kind: 'intra-phase' | 'wye-neutral' | 'delta-cycle' | 'phase-chain' | 'independent-tail';
  phase: number;
  layer: CopperLayer;            // Phase 2: home layer (F.Cu / In1 / In2 / B.Cu)
  startX: number; startY: number;
  endX: number;   endY: number;
  radius: number;
  sweepCW: boolean;
}

export interface BusSegment {
  kind: 'pad-to-arc' | 'arc-to-pad' | 'neutral-spoke';
  phase: number;
  layer: CopperLayer;            // inner intra-phase: F.Cu; outer bus: its net's pool layer (2026-07-04)
  startX: number; startY: number;
  endX: number;   endY: number;
}

export interface BusRouting {
  arcs: BusArc[];
  segments: BusSegment[];
  bridgeVias: BridgeVia[];       // Phase 2: 2 per non-F.Cu INNER arc (outer bus is bridge-free, Task 4b)
  neutralNode: { x: number; y: number } | null;
  /** Entry-pad holes that participate in an outer bus and need a selective pad on a
   *  specific copper layer — the plated T1 hole is the F.Cu↔layer transition.
   *  Outer-bus participants (wye/delta/phase-chain joins) land on their net's
   *  pool-allocated layer (In1/In2/B.Cu, corrective 2026-07-04). Generalizes the
   *  old B.Cu-only `backPadTerminals`.
   *  `padClass` distinguishes entry-terminal holes ('terminal', D12, mask-opened)
   *  from internal join-via pads ('via', D11, no mask) added by the k≥4 inner-join
   *  pool allocator on In1/In2 exit-via holes. */
  extraPadTerminals: Array<{ x: number; y: number; layer: CopperLayer; padClass: 'terminal' | 'via' }>;
  /** True when delta was requested (even k) but the derived arc-radius window
   *  failed to close — NO delta bus copper was emitted (open circuit). */
  deltaBusDropped: boolean;
  /** True when the inner margin could not fit every series join — some coil
   *  connections were NOT generated (open circuit). */
  innerJoinsDropped: boolean;
  /** True when the shared outer-margin bus pool could not fit every outer-bus net
   *  (wye/delta cycles/phase-chain joins) — one or more whole nets were NOT
   *  generated (open circuit). Wye is dropped last (priority: wye > delta > joins).
   *  Distinct from `deltaBusDropped`, which means the delta radius window itself
   *  failed to close; both may fire. */
  outerJoinsDropped: boolean;
  /** Count of outer-pool slots reserved for N>=6 hookup boundaries (corrective
   *  amendment, 2026-07-06) — 0 at N=4. These slots are closed to bus nets on
   *  EVERY layer (hookups admit ahead of wye/delta/joins); if hookups fit but
   *  starve the remaining pool, `outerJoinsDropped`/`deltaBusDropped` fire via
   *  the existing mechanism (Amendment II's two-tier honest drop). */
  hookupSlotCount: number;
}

function phaseStaggeredRadius(
  safeMin: number,
  safeSpan: number,
  phase: number,
  numPhases: number,
): number {
  return safeMin + safeSpan * (phase + 1) / (numPhases + 1);
}

function polarPoint(radius: number, angle: number): { x: number; y: number } {
  return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
}

export function computeBusRouting(
  params: StatorParams,
  _phaseLabels: readonly string[],
  windingType: WindingType,
  coilShape: CoilShape = 'spiral',
  flips?: boolean[],
  // Terminal-pad/hole sizes for delta cycle-arc keep-away derivation. Defaults to the
  // 'solder' style (matches the component default and the DRC harness). No magic
  // numbers: the delta radius window is derived from these + trace/via geometry.
  termDims: { padMm: number; holeMm: number } = { padMm: 1.5, holeMm: 0.8 },
  // Terminal layout (6-lead 'independent' only). 'adjacent' (default) routes each
  // phase's − end to a return pad beside its drive pad — byte-identical to before.
  // 'stacking-tabs' routes − ends out to the − stacking ears and stubs the drive
  // pads out to the + ears; it applies ONLY when `assessEarLayout` is valid,
  // otherwise it falls back to 'adjacent' (honest plain-circle outline elsewhere).
  opts: { terminalLayout?: 'adjacent' | 'stacking-tabs' } = {},
): BusRouting {
  const arcs: BusArc[] = [];
  const segments: BusSegment[] = [];
  const bridgeVias: BridgeVia[] = [];
  const extraPadTerminals: Array<{ x: number; y: number; layer: CopperLayer; padClass: 'terminal' | 'via' }> = [];
  let neutralNode: { x: number; y: number } | null = null;
  let deltaBusDropped = false;
  let innerJoinsDropped = false;
  let outerJoinsDropped = false;
  // Hookup boundaries (N>=6) admit ahead of every bus net — their slots are
  // reserved wholesale (closed to bus copper on ANY layer) so a hookup via's
  // hole can never cross bus copper (amendment II, probe-verified hazard C).
  const hookupReserved = hookupSlotPlan(params, termDims);
  const hookupSlotCount = hookupReserved.slots.length;
  const reservedSlots = new Set(hookupReserved.slots.map(h => h.slot));

  // Task 4b: only INNER intra-phase arcs bridge to F.Cu. The outer bus lives on
  // B.Cu and transitions through the plated entry hole, so it needs no bridges.
  function emitBridgeIfNeeded(arc: BusArc, phase: number): void {
    if (arc.layer === 'F.Cu') return;
    if (arc.kind === 'wye-neutral' || arc.kind === 'delta-cycle' || arc.kind === 'phase-chain') return;
    const id = `${arc.kind}-${phase}`;
    bridgeVias.push({
      position: { x: arc.startX, y: arc.startY },
      layers: ['F.Cu', arc.layer],
      tool: 0.4,
      arcId: `${id}-start`,
    });
    bridgeVias.push({
      position: { x: arc.endX, y: arc.endY },
      layers: ['F.Cu', arc.layer],
      tool: 0.4,
      arcId: `${id}-end`,
    });
  }

  const { numCoils, numPhases, innerRadius, innerMargin, traceClearance } = params;
  const coilsPerPhase = Math.floor(numCoils / numPhases);
  if (coilsPerPhase < 1) {
    return { arcs, segments, bridgeVias, neutralNode, extraPadTerminals, deltaBusDropped, innerJoinsDropped, outerJoinsDropped, hookupSlotCount };
  }

  const limits = computeTurnLimits(params, coilShape);
  const n = limits.total;
  if (n < 2) return { arcs, segments, bridgeVias, neutralNode, extraPadTerminals, deltaBusDropped, innerJoinsDropped, outerJoinsDropped, hookupSlotCount };

  const effectiveFlips = flips ?? planCoilPolarities(params).flip;

  const coilInner = innerRadius + innerMargin;

  const innerSafeMin = innerRadius + traceClearance;
  const innerSafeMax = coilInner - traceClearance;
  const innerSafeSpan = Math.max(0, innerSafeMax - innerSafeMin);

  // Outer-margin keep-aways (L1↔L2 via band, entry-hole/pad rings, board edge)
  // live in `outerBusBand`/`outerSlotRadius`/`maxOuterSlots` — the unified pool
  // below is the only outer-margin bus emitter (corrective 2026-07-04).

  // Build each phase's series chain; collect far ends for the wye/delta bus.
  const farEnds: ChainTerminal[] = [];

  // Extra inner joins (k≥4) are pool-allocated after every phase is collected —
  // widest angular span → deepest slot (spec 2026-07-03 §2). Deferred here.
  interface DeferredJoin { p: number; aX: number; aY: number; bX: number; bY: number; span: number; }
  const deferredInner: DeferredJoin[] = [];

  // Outer-margin phase-chain joins (corrective 2026-07-04): ALL outer joins defer
  // into per-phase collections, one 'join-p<phase>' NET per phase. Same-phase joins
  // are span-disjoint by chain construction, so they share the phase's single
  // allocated (layer, slot) pair. Emission is post-loop through the unified pool.
  interface DeferredOuterJoin { aX: number; aY: number; bX: number; bY: number; span: number; }
  const deferredOuterByPhase: DeferredOuterJoin[][] = Array.from({ length: numPhases }, () => []);

  const angSpan = (aAngle: number, bAngle: number): number => {
    const raw = Math.abs((((aAngle - bAngle) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI));
    return Math.min(raw, 2 * Math.PI - raw);
  };

  // Same-layer no-crossing bookkeeping (inner-allocator addendum 2026-07-04):
  // every inner join placed on a layer registers its terminal angles. A join's
  // radial descents run at exactly those angles, from the exit ring down to its
  // arc — so two same-layer joins are contact-free iff NEITHER join's terminal
  // angles fall strictly inside the other's arc span (a descent through a
  // foreign arc's span is a galvanic cross-phase short; measured at k=4:
  // phase 0's descent at 191.4° through phase 1's 108.6°–221.4° arc on In1).
  const placedInnerByLayer = new Map<CopperLayer, Array<{ aAng: number; bAng: number }>>();
  const registerInnerPlaced = (layer: CopperLayer, aAng: number, bAng: number): void => {
    if (!placedInnerByLayer.has(layer)) placedInnerByLayer.set(layer, []);
    placedInnerByLayer.get(layer)!.push({ aAng, bAng });
  };
  // Strict-interior test: theta lies inside the minor arc between aAng and bAng.
  const insideSpan = (theta: number, aAng: number, bAng: number): boolean => {
    const da = angSpan(aAng, theta), db = angSpan(theta, bAng);
    return da > 1e-9 && db > 1e-9 && da + db <= angSpan(aAng, bAng) + 1e-9;
  };
  const joinsCross = (
    j1: { aAng: number; bAng: number }, j2: { aAng: number; bAng: number },
  ): boolean =>
    insideSpan(j1.aAng, j2.aAng, j2.bAng) || insideSpan(j1.bAng, j2.aAng, j2.bAng) ||
    insideSpan(j2.aAng, j1.aAng, j1.bAng) || insideSpan(j2.bAng, j1.aAng, j1.bAng);

  const phaseChains: PhaseChain[] = [];
  for (let p = 0; p < numPhases; p++) {
    const phaseCoils: number[] = [];
    for (let c = 0; c < numCoils; c++) if (c % numPhases === p) phaseCoils.push(c);
    const chain = planPhaseChain(phaseCoils, params, coilShape, n, effectiveFlips);
    phaseChains.push(chain);
    farEnds.push(chain.farEnd);

    let innerOrdinal = 0;
    for (const join of chain.joins) {
      const aAngle = Math.atan2(join.a.y, join.a.x);
      const bAngle = Math.atan2(join.b.y, join.b.x);

      if (join.margin === 'inner') {
        if (innerOrdinal === 0) {
          // First inner join per phase sits on the fixed-pitch slot ladder (Task 4b):
          // slot p, one per phase — the top slot is exitTerminalGeom's `topArc`.
          const arcRadius = innerSlotRadius(params, p);
          const layer = busArcHomeLayer('intra-phase', p);
          const arcStart = polarPoint(arcRadius, aAngle);
          const arcEnd = polarPoint(arcRadius, bAngle);
          segments.push({ kind: 'pad-to-arc', phase: p, layer: 'F.Cu', startX: join.a.x, startY: join.a.y, endX: arcStart.x, endY: arcStart.y });
          segments.push({ kind: 'arc-to-pad', phase: p, layer: 'F.Cu', startX: arcEnd.x, startY: arcEnd.y, endX: join.b.x, endY: join.b.y });
          arcs.push({
            kind: 'intra-phase', phase: p, layer,
            startX: arcStart.x, startY: arcStart.y, endX: arcEnd.x, endY: arcEnd.y,
            radius: arcRadius,
            sweepCW: shorterSweepCW(arcStart.x, arcStart.y, arcEnd.x, arcEnd.y),
          });
          emitBridgeIfNeeded(arcs[arcs.length - 1], p);
          // First joins participate in the no-crossing constraint too: an extra's
          // descent through a first-join arc's span is the same short class.
          registerInnerPlaced(layer, aAngle, bAngle);
        } else {
          // Extra inner joins are pool-allocated after all phases are collected
          // (widest span → deepest slot). Emission happens post-loop, so defer.
          deferredInner.push({
            p, aX: join.a.x, aY: join.a.y, bX: join.b.x, bY: join.b.y,
            span: angSpan(aAngle, bAngle),
          });
        }
        innerOrdinal++;
      } else {
        // Outer-margin phase-chain join → phase p's 'join-p<phase>' net (deferred
        // to the unified outer pool; no per-arc radius here anymore).
        deferredOuterByPhase[p].push({
          aX: join.a.x, aY: join.a.y, bX: join.b.x, bY: join.b.y,
          span: angSpan(aAngle, bAngle),
        });
      }
    }
  }

  // ── Extra inner joins: (layer, slot) pool allocation (spec 2026-07-03 §2) ──
  // Pool = all (non-F.Cu layer × slot) pairs below the exit ring, minus the
  // per-phase first-join pairs. Widest angular span → deepest (lowest-radius)
  // slot; joins beyond capacity are dropped (innerJoinsDropped).
  const slots = maxInnerSlots(params, coilShape, n);
  const firstJoinPairs = new Set<string>();
  for (let p = 0; p < numPhases; p++) {
    // First joins sit at slot p on busArcHomeLayer('intra-phase', p) by construction.
    firstJoinPairs.add(`${busArcHomeLayer('intra-phase', p)}|${p}`);
  }

  // Layer preference: In1 → In2 → B.Cu (unchanged order). B.Cu is a full pool
  // member (inner-allocator addendum 2026-07-04): its old "last resort, DRC
  // can't classify" caveat (sub-project 2) is superseded — a B.Cu inner join
  // intentionally fuses its own phase's B.Cu winding exits, and the join-net
  // seeds + `net:join-p` topology group now classify that as same-net. Its arc
  // sits below the exit-tail band (maxInnerSlots ceiling) and its descents run
  // at its own terminals only, so foreign contact is impossible (asserted in
  // pcb-stator-bus-routing.spec). Slot-ascending within a layer preserves the
  // widest-span → deepest-slot invariant among the joins a layer accepts.
  const NON_F = nonFPoolLayers(params.numLayers);
  const freePairs: Array<{ layer: CopperLayer; slot: number }> = [];
  for (const layer of NON_F) {
    for (let slot = 0; slot < slots; slot++) {
      if (!firstJoinPairs.has(`${layer}|${slot}`)) freePairs.push({ layer, slot });
    }
  }

  // First-fit under the same-layer no-crossing constraint (addendum 2026-07-04):
  // widest join first; each takes the first free pair whose LAYER has no
  // registered join (first joins included) crossing it. A join no pair accepts
  // is dropped WHOLE (innerJoinsDropped) — never a partial placement.
  deferredInner.sort((a, b) => b.span - a.span);
  for (const j of deferredInner) {
    const aAngle = Math.atan2(j.aY, j.aX);
    const bAngle = Math.atan2(j.bY, j.bX);
    const me = { aAng: aAngle, bAng: bAngle };
    const idx = freePairs.findIndex(pair =>
      (placedInnerByLayer.get(pair.layer) ?? []).every(k => !joinsCross(me, k)));
    if (idx === -1) { innerJoinsDropped = true; continue; }
    const pair = freePairs.splice(idx, 1)[0];
    const arcRadius = innerSlotRadius(params, pair.slot);
    const arcStart = polarPoint(arcRadius, aAngle);
    const arcEnd = polarPoint(arcRadius, bAngle);
    segments.push({ kind: 'pad-to-arc', phase: j.p, layer: pair.layer, startX: j.aX, startY: j.aY, endX: arcStart.x, endY: arcStart.y });
    segments.push({ kind: 'arc-to-pad', phase: j.p, layer: pair.layer, startX: arcEnd.x, startY: arcEnd.y, endX: j.bX, endY: j.bY });
    arcs.push({
      kind: 'intra-phase', phase: j.p, layer: pair.layer,
      startX: arcStart.x, startY: arcStart.y, endX: arcEnd.x, endY: arcEnd.y,
      radius: arcRadius,
      sweepCW: shorterSweepCW(arcStart.x, arcStart.y, arcEnd.x, arcEnd.y),
    });
    registerInnerPlaced(pair.layer, aAngle, bAngle);
    // Layer transition at the exit-via holes: In1/In2 need selective via-class
    // pads (the hole IS the transition, no F.Cu bridge); B.Cu and F.Cu are already
    // in every exit via's pad set.
    if (pair.layer === 'In1.Cu' || pair.layer === 'In2.Cu') {
      extraPadTerminals.push({ x: j.aX, y: j.aY, layer: pair.layer, padClass: 'via' });
      extraPadTerminals.push({ x: j.bX, y: j.bY, layer: pair.layer, padClass: 'via' });
    }
  }

  // ── Unified outer-margin bus pool (corrective 2026-07-04) ─────────────────
  // Every outer-margin bus NET (the whole wye tree, each delta cycle, each
  // phase's outer joins) allocates ONE (radius-slot × layer) pair from a shared
  // pool strictly ABOVE the entry-pad keep-away band (`outerBusBand`), mirroring
  // the inner allocator. One pair per NET — same-net copper may self-overlap
  // (harmless); different nets never share a pair, so the k≥4 cross-phase join
  // ribbon (three phases' arcs fusing on one B.Cu radius band) cannot form.
  // Priority (drop last-first): wye > delta cycles > join nets — a motor without
  // its neutral is dead, so the wye net is never the one dropped if any pair
  // exists; dropped joins/cycles are survivable and banner-flagged. Within a
  // tier, wider total angular coverage allocates first (deeper slot).
  interface OuterNet {
    tier: number;       // 0 wye, 1 delta cycle, 2 phase-chain join
    coverage: number;   // total angular span (rad) of the net's arcs
    phase: number;      // stable tie-break + emission identity
    emit: (layer: CopperLayer, radius: number) => void;
  }
  const outerNets: OuterNet[] = [];

  if (windingType === 'wye') {
    const margin = farEnds[0].margin;
    const farAngles = farEnds.map(f => Math.atan2(f.y, f.x));
    const neutralAngle = circularMeanAngle(farAngles);

    if (margin === 'inner') {
      // Odd k: far ends are inner exit vias — the wye bus lives on the INNER
      // margin (unchanged by the 2026-07-04 corrective, which is outer-only).
      const neutralRadius = innerSafeMin + innerSafeSpan * 0.9;
      neutralNode = { x: neutralRadius * Math.cos(neutralAngle), y: neutralRadius * Math.sin(neutralAngle) };

      for (let p = 0; p < numPhases; p++) {
        const far = farEnds[p];
        const padAngle = Math.atan2(far.y, far.x);
        const arcRadius = phaseStaggeredRadius(innerSafeMin, innerSafeSpan, p, numPhases);
        const { x: arcStartX, y: arcStartY } = polarPoint(arcRadius, padAngle);
        const { x: arcEndX,   y: arcEndY   } = polarPoint(arcRadius, neutralAngle);

        // Task 4b: the wye bus (pad-to-arc, arc, neutral-spoke) rides B.Cu; the
        // exit via is its plated F.Cu↔B.Cu transition.
        segments.push({ kind: 'pad-to-arc', phase: p, layer: 'B.Cu', startX: far.x, startY: far.y, endX: arcStartX, endY: arcStartY });
        extraPadTerminals.push({ x: far.x, y: far.y, layer: 'B.Cu', padClass: 'terminal' });

        const sweepCW = shorterSweepCW(arcStartX, arcStartY, arcEndX, arcEndY);
        const aS = Math.atan2(arcStartY, arcStartX), aE = Math.atan2(arcEndY, arcEndX);
        let swept = sweepCW ? (aE - aS) : (aS - aE);
        swept = ((swept % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        if (swept > 1e-3) {
          arcs.push({ kind: 'wye-neutral', phase: p, layer: busArcHomeLayer('wye-neutral', p), startX: arcStartX, startY: arcStartY, endX: arcEndX, endY: arcEndY, radius: arcRadius, sweepCW });
          emitBridgeIfNeeded(arcs[arcs.length - 1], p);
        }

        segments.push({ kind: 'neutral-spoke', phase: p, layer: 'B.Cu', startX: arcEndX, startY: arcEndY, endX: neutralNode!.x, endY: neutralNode!.y });
      }
    } else {
      // Even k: far ends are outer entries — the whole wye tree is ONE pool net.
      // The old per-phase staggered radii sat INSIDE the entry-pad band (a
      // cross-net short class at k≥4, safe at k=2 only because wye is one net);
      // all arcs now collapse to the single allocated slot radius, and the
      // neutral node sits ON that radius at the far-end centroid angle, so the
      // spokes vanish (the arcs meet at the node).
      const coverage = farAngles.reduce((s, a) => s + angSpan(a, neutralAngle), 0);
      outerNets.push({
        tier: 0, coverage, phase: -1,
        emit: (layer: CopperLayer, radius: number) => {
          neutralNode = polarPoint(radius, neutralAngle);
          for (let p = 0; p < numPhases; p++) {
            const far = farEnds[p];
            const padAngle = Math.atan2(far.y, far.x);
            const { x: arcStartX, y: arcStartY } = polarPoint(radius, padAngle);
            const { x: arcEndX,   y: arcEndY   } = polarPoint(radius, neutralAngle);

            // The free-entry hole is the plated F.Cu↔layer transition.
            segments.push({ kind: 'pad-to-arc', phase: p, layer, startX: far.x, startY: far.y, endX: arcStartX, endY: arcStartY });
            extraPadTerminals.push({ x: far.x, y: far.y, layer, padClass: 'terminal' });

            const sweepCW = shorterSweepCW(arcStartX, arcStartY, arcEndX, arcEndY);
            const aS = Math.atan2(arcStartY, arcStartX), aE = Math.atan2(arcEndY, arcEndX);
            let swept = sweepCW ? (aE - aS) : (aS - aE);
            swept = ((swept % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
            // Degenerate-arc guard stays: a far end on the centroid connects
            // through its pad-to-arc segment alone.
            if (swept > 1e-3) {
              arcs.push({ kind: 'wye-neutral', phase: p, layer, startX: arcStartX, startY: arcStartY, endX: arcEndX, endY: arcEndY, radius, sweepCW });
            }
          }
        },
      });
    }
  }

  else if (windingType === 'delta') {
    const even = coilsPerPhase % 2 === 0;
    if (even) {
      // Each delta cycle (far end of phase p → drive of phase p+1, both outer
      // entries) is one pool net. The old inline radius-window derivation is
      // hoisted into `outerBusBand` (ONE source, shared by every outer net); a
      // band that cannot fit even one slot is the delta window failure —
      // deltaBusDropped keeps exactly that meaning, and the (empty) pool also
      // drops the nets whole (outerJoinsDropped). Both may fire.
      if (maxOuterSlots(params, termDims) === 0) deltaBusDropped = true;
      for (let p = 0; p < numPhases; p++) {
        const from = farEnds[p];                                  // outer entry of phase p's last coil
        const to = phaseChains[(p + 1) % numPhases].driveEnd;     // outer entry of next phase's first coil
        const fromAngle = Math.atan2(from.y, from.x);
        const toAngle = Math.atan2(to.y, to.x);
        outerNets.push({
          tier: 1, coverage: angSpan(fromAngle, toAngle), phase: p,
          emit: (layer: CopperLayer, radius: number) => {
            const { x: arcStartX, y: arcStartY } = polarPoint(radius, fromAngle);
            const { x: arcEndX,   y: arcEndY   } = polarPoint(radius, toAngle);
            // One net per (layer, slot) pair: the two segments (pad↔arc) and the
            // two participating entry pads all live on THIS net's allocated layer,
            // so foreign same-layer pad overlap remains impossible.
            segments.push({ kind: 'pad-to-arc', phase: p, layer, startX: from.x, startY: from.y, endX: arcStartX, endY: arcStartY });
            arcs.push({ kind: 'delta-cycle', phase: p, layer, startX: arcStartX, startY: arcStartY, endX: arcEndX, endY: arcEndY, radius, sweepCW: shorterSweepCW(arcStartX, arcStartY, arcEndX, arcEndY) });
            segments.push({ kind: 'arc-to-pad', phase: p, layer, startX: arcEndX, startY: arcEndY, endX: to.x, endY: to.y });
            extraPadTerminals.push({ x: from.x, y: from.y, layer, padClass: 'terminal' });
            extraPadTerminals.push({ x: to.x, y: to.y, layer, padClass: 'terminal' });
          },
        });
      }
    }
    // odd k: delta is unsupported (far end is inner, next drive is outer → would cross
    // the coil band). Emit no delta arcs; assessConfigSupport reports this as broken via
    // the odd-k rule. deltaBusDropped stays false — this is by design, not a window failure.
  }

  else if (windingType === 'independent') {
    // 6-lead termination: NO inter-phase bus at all (no neutral node, no delta
    // cycles). Each phase is still series-chained by the shared inner/outer joins
    // above; here we route each phase's free end (its − end after chaining) OUT.
    //
    // Two terminal layouts (see `opts.terminalLayout`):
    //  • 'adjacent' (default): the − end goes to a return pad drilled beside that
    //    phase's drive (+) pad. Byte-identical to before.
    //  • 'stacking-tabs' (valid ear layout only): the − end goes out to that phase's
    //    − stacking ear, and the drive pad stubs out to the + ear. This lets identical
    //    boards stack hole-to-hole (see pcb-stator-stacking-tabs).
    // Both are one outer-pool NET per phase (tier 0 — a return lead is as essential
    // as a neutral, so it is never the preferentially-dropped net).
    const useStacking =
      opts.terminalLayout === 'stacking-tabs' && assessEarLayout(params, termDims).valid;

    if (!useStacking) {
      // Adjacent return pads: pad-to-arc up to the allocated slot, an arc along the
      // slot to the return-pad azimuth, then a radial leg down to the return-pad
      // hole. Both plated holes (free end and return pad) are F.Cu↔slot-layer
      // transitions. Honest fallback for an invalid stacking-tabs request too.
      const offset = returnPadOffsetRad(params, termDims.padMm);
      for (let p = 0; p < numPhases; p++) {
        const free = farEnds[p];
        const freeAngle = Math.atan2(free.y, free.x);
        const drive = phaseChains[p].driveEnd;
        const retAngle = Math.atan2(drive.y, drive.x) + offset;
        const retPad = polarPoint(entryPadRadius(params), retAngle);
        outerNets.push({
          tier: 0, coverage: angSpan(freeAngle, retAngle), phase: p,
          emit: (layer: CopperLayer, radius: number) => {
            const arcStart = polarPoint(radius, freeAngle);
            const arcEnd = polarPoint(radius, retAngle);
            // Free-end plated hole = F.Cu(coil entry) ↔ slot-layer transition.
            segments.push({ kind: 'pad-to-arc', phase: p, layer, startX: free.x, startY: free.y, endX: arcStart.x, endY: arcStart.y });
            extraPadTerminals.push({ x: free.x, y: free.y, layer, padClass: 'terminal' });
            arcs.push({
              kind: 'independent-tail', phase: p, layer,
              startX: arcStart.x, startY: arcStart.y, endX: arcEnd.x, endY: arcEnd.y,
              radius, sweepCW: shorterSweepCW(arcStart.x, arcStart.y, arcEnd.x, arcEnd.y),
            });
            segments.push({ kind: 'arc-to-pad', phase: p, layer, startX: arcEnd.x, startY: arcEnd.y, endX: retPad.x, endY: retPad.y });
            // Return-pad plated hole = F.Cu(return pad) ↔ slot-layer transition. The
            // F.Cu return pad itself joins GerberData.terminals via computeReturnPads.
            extraPadTerminals.push({ x: retPad.x, y: retPad.y, layer, padClass: 'terminal' });
          },
        });
      }
    } else {
      // Stacking-tabs: route each phase out to its ears. Same coordinates the
      // emitter's ear terminals use (via `computeStackingEars`), so the copper
      // lands hole-on-hole with the ear pads.
      const ears = computeStackingEars(params, termDims);
      for (let p = 0; p < numPhases; p++) {
        const plus = ears.find(e => e.phase === p && e.polarity === 'plus')!;
        const minus = ears.find(e => e.phase === p && e.polarity === 'minus')!;
        const pC = plus.holes[1];    // ear center hole (offsets [−3°, 0°, +3°])
        const mC = minus.holes[1];

        // + ear: short radial F.Cu stub from the drive pad out to the + ear centre,
        // then F.Cu connectors joining the centre hole to its two side holes so all
        // three ear holes are one net on F.Cu. No layer transition (drive pad and
        // ear pads are all F.Cu), so no extraPadTerminal.
        const drivePad = polarPoint(entryPadRadius(params), plus.azimuthRad);
        segments.push({ kind: 'pad-to-arc', phase: p, layer: 'F.Cu', startX: drivePad.x, startY: drivePad.y, endX: pC[0], endY: pC[1] });
        segments.push({ kind: 'arc-to-pad', phase: p, layer: 'F.Cu', startX: pC[0], startY: pC[1], endX: plus.holes[0][0], endY: plus.holes[0][1] });
        segments.push({ kind: 'arc-to-pad', phase: p, layer: 'F.Cu', startX: pC[0], startY: pC[1], endX: plus.holes[2][0], endY: plus.holes[2][1] });

        // − ear: pool-routed tail from the free end out to the − ear centre (radial
        // leg reaches PAST the board edge onto the ear protrusion), plus F.Cu
        // connectors to the side holes. The plated centre hole is the pool↔F.Cu
        // transition; extraPadTerminals gives it the selective pool-layer pad
        // (the return-pad pattern).
        const free = farEnds[p];
        const freeAngle = Math.atan2(free.y, free.x);
        outerNets.push({
          tier: 0, coverage: angSpan(freeAngle, minus.azimuthRad), phase: p,
          emit: (layer: CopperLayer, radius: number) => {
            const arcStart = polarPoint(radius, freeAngle);
            const arcEnd = polarPoint(radius, minus.azimuthRad);
            segments.push({ kind: 'pad-to-arc', phase: p, layer, startX: free.x, startY: free.y, endX: arcStart.x, endY: arcStart.y });
            extraPadTerminals.push({ x: free.x, y: free.y, layer, padClass: 'terminal' });
            arcs.push({
              kind: 'independent-tail', phase: p, layer,
              startX: arcStart.x, startY: arcStart.y, endX: arcEnd.x, endY: arcEnd.y,
              radius, sweepCW: shorterSweepCW(arcStart.x, arcStart.y, arcEnd.x, arcEnd.y),
            });
            segments.push({ kind: 'arc-to-pad', phase: p, layer, startX: arcEnd.x, startY: arcEnd.y, endX: mC[0], endY: mC[1] });
            extraPadTerminals.push({ x: mC[0], y: mC[1], layer, padClass: 'terminal' });
          },
        });
        // − ear F.Cu connectors (centre → side holes) — always F.Cu, independent
        // of which pool layer the tail lands on.
        segments.push({ kind: 'arc-to-pad', phase: p, layer: 'F.Cu', startX: mC[0], startY: mC[1], endX: minus.holes[0][0], endY: minus.holes[0][1] });
        segments.push({ kind: 'arc-to-pad', phase: p, layer: 'F.Cu', startX: mC[0], startY: mC[1], endX: minus.holes[2][0], endY: minus.holes[2][1] });
      }
    }
  }

  // Outer phase-chain joins: one net per phase. Same-phase joins are span-disjoint
  // by chain construction (they connect consecutive same-phase coils in disjoint
  // angular sectors), so they legitimately share the phase's single allocated pair.
  for (let p = 0; p < numPhases; p++) {
    const joins = deferredOuterByPhase[p];
    if (joins.length === 0) continue;
    outerNets.push({
      tier: 2, coverage: joins.reduce((s, j) => s + j.span, 0), phase: p,
      emit: (layer: CopperLayer, radius: number) => {
        for (const j of joins) {
          const aAngle = Math.atan2(j.aY, j.aX);
          const bAngle = Math.atan2(j.bY, j.bX);
          const arcStart = polarPoint(radius, aAngle);
          const arcEnd = polarPoint(radius, bAngle);
          segments.push({ kind: 'pad-to-arc', phase: p, layer, startX: j.aX, startY: j.aY, endX: arcStart.x, endY: arcStart.y });
          segments.push({ kind: 'arc-to-pad', phase: p, layer, startX: arcEnd.x, startY: arcEnd.y, endX: j.bX, endY: j.bY });
          arcs.push({
            kind: 'phase-chain', phase: p, layer,
            startX: arcStart.x, startY: arcStart.y, endX: arcEnd.x, endY: arcEnd.y,
            radius,
            sweepCW: shorterSweepCW(arcStart.x, arcStart.y, arcEnd.x, arcEnd.y),
          });
          // Both joined entry holes transition through their plated pad to the layer.
          extraPadTerminals.push({ x: j.aX, y: j.aY, layer, padClass: 'terminal' });
          extraPadTerminals.push({ x: j.bX, y: j.bY, layer, padClass: 'terminal' });
        }
      },
    });
  }

  // Allocation: pairs sorted slot-ascending, layer In1→In2→B.Cu within a slot
  // (F.Cu holds the entry connectors/pads exclusively); nets sorted by tier then
  // coverage descending; lockstep. Nets beyond capacity are dropped WHOLE — no
  // partial copper (an open circuit is honest; a fused short is not) — and flag
  // outerJoinsDropped. Hookup-reserved slots (amendment II) are skipped
  // WHOLESALE — on every layer — since a hookup via's plated hole at that
  // radius must not cross bus copper on any layer.
  const OUTER_LAYERS = nonFPoolLayers(params.numLayers);
  const outerSlots = maxOuterSlots(params, termDims);
  const outerPairs: Array<{ layer: CopperLayer; slot: number }> = [];
  for (let slot = 0; slot < outerSlots; slot++) {
    if (reservedSlots.has(slot)) continue;
    for (const layer of OUTER_LAYERS) outerPairs.push({ layer, slot });
  }
  outerNets.sort((a, b) => a.tier - b.tier || b.coverage - a.coverage || a.phase - b.phase);
  for (let i = 0; i < outerNets.length; i++) {
    const pair = outerPairs[i];
    if (!pair) { outerJoinsDropped = true; break; }
    outerNets[i].emit(pair.layer, outerSlotRadius(params, termDims, pair.slot));
  }

  return { arcs, segments, bridgeVias, neutralNode, extraPadTerminals, deltaBusDropped, innerJoinsDropped, outerJoinsDropped, hookupSlotCount };
}
